export const USA_REGION_NAME = 'United States of America (USA)';

export interface RegionDivisionMapping {
  regionName: string;
  divisionNames: string[];
}

export const REGION_HIERARCHY: RegionDivisionMapping[] = [
  {
    regionName: 'Northeast',
    divisionNames: ['New England', 'Middle Atlantic'],
  },
  {
    regionName: 'Midwest',
    divisionNames: ['East North Central', 'West North Central'],
  },
  {
    regionName: 'South',
    divisionNames: ['South Atlantic', 'East South Central', 'West South Central'],
  },
  {
    regionName: 'West',
    divisionNames: ['Mountain', 'Pacific'],
  },
];

export const REGION_NAMES = REGION_HIERARCHY.map((r) => r.regionName);
export const DIVISION_NAMES = REGION_HIERARCHY.flatMap((r) => r.divisionNames);

/** Lookup: division name -> parent region name */
export const DIVISION_TO_REGION: Record<string, string> = {};
REGION_HIERARCHY.forEach(({ regionName, divisionNames }) => {
  divisionNames.forEach((div) => {
    DIVISION_TO_REGION[div] = regionName;
  });
});

/**
 * Compute optimal region IDs based on selection and mode.
 *
 * - `api` mode: Returns highest-level IDs only (parent region ID when all its
 *   divisions are selected, individual division IDs when partial). Entire US
 *   always included if selected.
 * - `geometry` mode: Same logic — parent boundary when fully selected, division
 *   boundaries when partial. Entire US if selected.
 */
export function computeOptimalRegionIds(
  selectedIds: number[],
  regionData: MedScout.Region[] | undefined,
  mode: 'geometry' | 'api'
): number[] {
  if (!regionData || selectedIds.length === 0) return selectedIds;

  const selectedSet = new Set(selectedIds);
  const result: number[] = [];

  // Build lookup maps from API data
  const regionByName = new Map<string, MedScout.Region>();
  regionData.forEach((r) => {
    regionByName.set(r.name, r);
  });

  // Find and include Entire US if selected
  const usaRegion = regionData.find(
    (r) => r.type === 'country' || r.name === USA_REGION_NAME
  );
  if (usaRegion && selectedSet.has(usaRegion.id)) {
    result.push(usaRegion.id);
  }

  // Process each region group
  for (const { regionName, divisionNames } of REGION_HIERARCHY) {
    const parentRegion = regionByName.get(regionName);
    const divisionRegions = divisionNames
      .map((name) => regionByName.get(name))
      .filter((r): r is MedScout.Region => r != null);

    if (!parentRegion || divisionRegions.length === 0) continue;

    const allDivisionsSelected = divisionRegions.every((d) =>
      selectedSet.has(d.id)
    );
    const someDivisionsSelected = divisionRegions.some((d) =>
      selectedSet.has(d.id)
    );

    if (allDivisionsSelected && selectedSet.has(parentRegion.id)) {
      // Fully selected: use parent ID only
      result.push(parentRegion.id);
    } else if (someDivisionsSelected) {
      // Partially selected: include individual division IDs
      divisionRegions.forEach((d) => {
        if (selectedSet.has(d.id)) {
          result.push(d.id);
        }
      });
    }
  }

  return result;
}
